import React from "react";
import { EnrichedProgram } from "@/common/course/types";
import useEmblaCarousel from "embla-carousel-react";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { ElementType } from "@/constants/tagular/main";
import useIsMobile from "@/hooks/useIsMobile";
import CardWrapper from "./CardWrapper";
import LoadingCard from "./LoadingCard";
import { useCarouselMessages } from "@/i18n/hooks/useCarouselMessages";
import useTranslations from "@/i18n/useTranslations";

type CardType = "product-card" | "program-card" | "new-product-card";
type NavigationType = "default" | "individual-buttons";

interface CenteredCarouselProps {
  items: EnrichedProgram[];
  getUrl: (path?: string) => string | undefined;
  cardType?: CardType;
  navigationType?: NavigationType;
  isLoading?: boolean;
  hasShadow?: boolean;
}

function CenteredCarousel({
  items,
  getUrl,
  cardType,
  navigationType = "default",
  isLoading = false,
  hasShadow,
}: CenteredCarouselProps) {
  const tCarousel = useTranslations("Carousel");

  const carouselMessages = useCarouselMessages();

  const [emblaRef, emblaApi] = useEmblaCarousel({
    slidesToScroll: 1,
    containScroll: "trimSnaps",
    align: "start",
    dragFree: false,
    breakpoints: {
      "(min-width: 640px)": {
        slidesToScroll: 1,
      },
      "(min-width: 768px)": {
        slidesToScroll: 1,
      },
      "(min-width: 1024px)": {
        slidesToScroll: 1,
      },
    },
  });

  // Element events for navigation buttons
  const { elementClicked: prevButtonClicked } = useElementEvents({
    webElement: {
      elementType: ElementType.Button,
      name: "program-carousel-prev",
      location: "program-carousel",
      text: "Previous slide",
      htmlId: "program-carousel-prev",
    },
  });

  const { elementClicked: nextButtonClicked } = useElementEvents({
    webElement: {
      elementType: ElementType.Button,
      name: "program-carousel-next",
      location: "program-carousel",
      text: "Next slide",
      htmlId: "program-carousel-next",
    },
  });

  // Element events for carousel swiping
  const { elementClicked: carouselSwiped } = useElementEvents({
    webElement: {
      elementType: ElementType.Component,
      name: "program-carousel-swipe",
      location: "program-carousel",
      text: "Carousel swiped",
      htmlId: "program-carousel-swipe",
    },
  });

  // Element events for individual card button clicks
  const { elementClicked: cardButtonClicked } = useElementEvents({
    webElement: {
      elementType: ElementType.Button,
      name: "program-carousel-card-button",
      location: "program-carousel",
      text: "Go to specific card",
      htmlId: "program-carousel-card-button",
    },
  });

  const scrollToCard = React.useCallback(
    (index: number) => {
      if (emblaApi) {
        emblaApi.scrollTo(index);
        cardButtonClicked();
      }
    },
    [emblaApi, cardButtonClicked],
  );

  const [prevBtnDisabled, setPrevBtnDisabled] = React.useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = React.useState(true);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [lastOne, setLastOne] = React.useState(false);
  const [isAtEnd, setIsAtEnd] = React.useState(false);
  const isMobile = useIsMobile();

  // When there's a single item (and not loading) we want it to stretch full width
  const singleItem = !isLoading && items.length === 1;
  // When there are exactly two items (and not loading) we want them to split
  // the carousel space 50/50 — but only on non-mobile viewports. On mobile
  // devices we should keep a single item per view.
  const twoItems = !isLoading && items.length === 2 && !isMobile;

  // Classes applied to each slide. For a single item we make it full width.
  // For two items we force 50% width each. Otherwise preserve previous
  // responsive sizing.
  const slideClasses = singleItem
    ? `embla__slide flex-[0_0_100%] min-h-[353px] min-w-0 ${isMobile ? "pr-4" : "pr-16"}`
    : twoItems
      ? "embla__slide flex-[0_0_50%] min-h-[353px] min-w-0 pr-4"
      : "embla__slide flex-[0_0_100%] sm:flex-[0_0_50%] md:flex-[0_0_40%] lg:flex-[0_0_33.333%] min-h-[353px] min-w-0 pr-4";

  const scrollPrev = React.useCallback(() => {
    if (emblaApi && !prevBtnDisabled) {
      emblaApi.scrollPrev();
      prevButtonClicked();
    }
  }, [emblaApi, prevButtonClicked, prevBtnDisabled]);

  const scrollNext = React.useCallback(() => {
    if (emblaApi && !nextBtnDisabled) {
      emblaApi.scrollNext();
      nextButtonClicked();
    }
  }, [emblaApi, nextButtonClicked, nextBtnDisabled]);

  // Track if we already triggered the swipe event for the current interaction
  const hasTriggeredSwipeEvent = React.useRef(false);

  // Store the carouselSwiped function in a ref to avoid dependency issues
  const carouselSwipedRef = React.useRef(carouselSwiped);
  carouselSwipedRef.current = carouselSwiped;

  React.useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setPrevBtnDisabled(!emblaApi.canScrollPrev());
      setNextBtnDisabled(!emblaApi.canScrollNext());
      setSelectedIndex(emblaApi.selectedScrollSnap());
      setIsAtEnd(!emblaApi.canScrollNext());

      // Reset the swipe event flag when slide selection changes
      hasTriggeredSwipeEvent.current = false;
    };

    const onScroll = () => {
      // Only trigger the swipe event once per interaction
      if (!hasTriggeredSwipeEvent.current) {
        carouselSwipedRef.current();
        hasTriggeredSwipeEvent.current = true;
      }
    };

    emblaApi.on("select", onSelect);
    emblaApi.on("scroll", onScroll);
    onSelect();

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("scroll", onScroll);
    };
  }, [emblaApi]);

  const renderLoadingCard = (index: number) => {
    return <LoadingCard key={`loading-${index}`} className={slideClasses} />;
  };

  // Helper function to render the appropriate card type
  const renderCard = (item: EnrichedProgram, index: number) => {
    const cardStyles = cardType === "program-card" ? "flex" : "";

    return (
      <CardWrapper
        key={item.uuid}
        hasShadow={hasShadow}
        item={item}
        index={index}
        getUrl={getUrl}
        cardType={cardType}
        className={`${slideClasses} ${cardStyles} !shadow-none`}
      />
    );
  };

  const renderItems = () => {
    if (isLoading) {
      return Array.from({ length: 3 }, (_, index) => renderLoadingCard(index));
    }
    return items.map((item, index) => renderCard(item, index));
  };

  return (
    <div
      className={`relative ${singleItem ? "w-full" : "max-w-[1040px] !mx-auto"}`}
    >
      {/* Left side arrow */}
      {!isLoading && !isMobile && items.length > 3 && (
        <button
          className={`absolute left-[-10px] top-[40%] transform -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center transition-colors ${
            prevBtnDisabled
              ? "text-gray-400 cursor-not-allowed"
              : "text-black hover:text-gray-600"
          }`}
          onClick={scrollPrev}
          disabled={prevBtnDisabled}
          aria-label={carouselMessages.previousSlide}
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12l4.58-4.59Z" />
          </svg>
        </button>
      )}

      <div
        className={`embla overflow-hidden ${singleItem ? "w-full" : "max-w-[950px] mx-auto"}`}
        ref={emblaRef}
      >
        <div className="embla__container flex">{renderItems()}</div>
      </div>

      {/* Right side arrow */}
      {!isLoading && !isMobile && items.length > 3 && (
        <button
          className={`absolute right-0 top-[40%] transform -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center transition-colors ${
            nextBtnDisabled
              ? "text-gray-400 cursor-not-allowed"
              : "text-black hover:text-gray-600"
          }`}
          onClick={scrollNext}
          disabled={nextBtnDisabled}
          aria-label={carouselMessages.nextSlide}
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8.59 16.59 13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
          </svg>
        </button>
      )}

      {/* Bottom navigation indicators */}
      {!isLoading && (items.length > 3 || (isMobile && items.length > 1)) && (
        <div className="flex justify-center items-center mt-8 space-x-2">
          {navigationType === "default" || isMobile ? (
            <>
              {/* Left arrow button */}
              <button
                className={`w-10 h-10 flex items-center justify-center transition-colors ${
                  prevBtnDisabled
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-black hover:text-gray-600"
                }`}
                onClick={scrollPrev}
                disabled={prevBtnDisabled}
                aria-label={carouselMessages.previousSlide}
              >
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12l4.58-4.59Z" />
                </svg>
              </button>

              {/* First indicator - selected when at the beginning */}
              <button
                className={`w-[18px] h-[18px] rounded-full transition-colors ${
                  selectedIndex === 0 && !isAtEnd
                    ? "bg-[#D74000]"
                    : "bg-[#00262B]"
                }`}
                aria-label={tCarousel("firstSlideIndicator")}
              />

              {/* Middle indicator - selected when not at beginning or end */}
              <button
                className={`w-[18px] h-[18px] rounded-full transition-colors ${
                  selectedIndex > 0 && !isAtEnd
                    ? "bg-[#D74000]"
                    : "bg-[#00262B]"
                }`}
                aria-label={tCarousel("middleSlidesIndicator")}
              />

              {/* Last indicator - selected when at the end */}
              <button
                className={`w-[18px] h-[18px] rounded-full transition-colors ${
                  isAtEnd ? "bg-[#D74000]" : "bg-[#00262B]"
                }`}
                aria-label={tCarousel("lastSlideIndicator")}
              />

              {/* Right arrow button */}
              <button
                className={`w-10 h-10 flex items-center justify-center transition-colors ${
                  nextBtnDisabled
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-black hover:text-gray-600"
                }`}
                onClick={scrollNext}
                disabled={nextBtnDisabled}
                aria-label={carouselMessages.nextSlide}
              >
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M8.59 16.59 13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                </svg>
              </button>
            </>
          ) : (
            <>
              <button
                className={`w-10 h-10 flex items-center justify-center transition-colors ${
                  prevBtnDisabled
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-black hover:text-gray-600"
                }`}
                onClick={scrollPrev}
                disabled={prevBtnDisabled}
                aria-label={carouselMessages.previousSlide}
              >
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12l4.58-4.59Z" />
                </svg>
              </button>
              {Array.from({ length: items.length - 2 }, (_, index) => (
                <button
                  key={index}
                  id="roundedMapButtons"
                  className={`w-[18px] h-[18px] rounded-full transition-colors ${
                    index === items.length - 2 && lastOne
                      ? "bg-[#D74000]"
                      : selectedIndex === index && !lastOne
                        ? "bg-[#D74000]"
                        : "bg-[#00262B]"
                  }`}
                  onClick={() => {
                    if (index === items.length - 2) {
                      setLastOne(true);
                    } else {
                      setLastOne(false);
                    }
                    scrollToCard(index);
                  }}
                  aria-label={`Go to card ${index + 1}`}
                />
              ))}
              <button
                className={`w-10 h-10 flex items-center justify-center transition-colors ${
                  nextBtnDisabled
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-black hover:text-gray-600"
                }`}
                onClick={scrollNext}
                disabled={nextBtnDisabled}
                aria-label={carouselMessages.nextSlide}
              >
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M8.59 16.59 13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                </svg>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default CenteredCarousel;
